import { useState, useEffect } from "react"
import { reservationStepPropTypes } from "source/registrations/propTypes"
import { isEmpty } from "lodash"
import SectionLayout from "source/registrations/reservations/SectionLayout"
import AddOnsForm from "./AddOnsForm"

const AddOns = ({
  attendees: reservationAttendees,
  error: reservationError,
  onReservationSave,
  addOns,
  isProcessing,
  onIsProcessing,
  onEdit,
  onCancel,
  step,
  className,
  reservation,
}) => {
  const [attendees, setAttendees] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    setAttendees(reservationAttendees)
  }, [reservationAttendees])

  const validAddOnOptions = () =>
    attendees
      .reduce(
        (array, attendee) =>
          array.concat(Object.values(attendee.addOnSelections)),
        [],
      )
      .every((attendeeSelections) => {
        const addOn = addOns.find(
          (a) => a.id === Number(attendeeSelections.addOnId),
        )

        const totalAvailable = attendeeSelections.addOnVariationId
          ? addOn.addOnVariations.find(
              (av) => av.id === Number(attendeeSelections.addOnVariationId),
            )?.totalAvailable
          : addOn?.totalAvailable

        const allowedQuantity =
          attendeeSelections.quantity > 0 &&
          attendeeSelections.quantity <= (totalAvailable || Infinity)

        return (
          (attendeeSelections.addOnableType === "AddOnVariation" &&
            attendeeSelections.addOnVariationId &&
            allowedQuantity) ||
          (attendeeSelections.addOnableType === "AddOn" &&
            attendeeSelections.addOnId &&
            allowedQuantity)
        )
      })

  const handleSave = async (_event) => {
    if (!validAddOnOptions()) {
      return setError(
        "Additional options can not be blank or less than available capacity",
      )
    }

    onIsProcessing(true)
    setError(null)

    const included = attendees
      .filter((a) => a.isAddOnModified && !isEmpty(a.addOnSelections))
      .reduce((addOnSelections, attendee) => {
        const attendeeAddOns = Object.values(attendee.addOnSelections).map(
          (addOnSelection) => ({
            type: "AddOnSelection",
            attributes: {
              attendeeId: attendee.id,
              addOnableType: addOnSelection.addOnableType,
              addOnableId:
                addOnSelection.addOnVariationId || addOnSelection.addOnId,
              quantity: addOnSelection.quantity || 1,
              _destroy: addOnSelection._destroy,
              id: addOnSelection.id,
            },
          }),
        )

        return addOnSelections.concat(attendeeAddOns)
      }, [])

    await onReservationSave({ data: { attributes: {} }, included })
    onIsProcessing(false)
  }

  const handleAttendeeAddOnChange = (changedAttendee, addOnSelections) => {
    setAttendees(
      attendees.map((attendee) =>
        attendee.id === changedAttendee.id
          ? {
              ...attendee,
              addOnSelections,
              isAddOnModified: true,
            }
          : attendee,
      ),
    )
  }

  const standardAttendees = attendees.filter((attendee) => !attendee.waitlist)

  const addOnError = error || reservationError

  return (
    <SectionLayout
      heading="Additional options"
      className={className}
      step={step}
      isButtonShowing={step.isEditable}
      onButtonClick={onEdit}
      error={addOnError}
      isNextDisabled={isProcessing || !validAddOnOptions()}
      onSave={handleSave}
      onCancel={onCancel}
      expiresAt={reservation.expiresAt}
      isProcessing={isProcessing}
    >
      {!step.isCurrent &&
        step.isComplete &&
        reservation.addOnSelections.length === 0 && (
          <div className="mb-2 c-tint2">None</div>
        )}

      {standardAttendees.map((attendee) => (
        <AddOnsForm
          key={attendee.id}
          attendee={attendee}
          addOns={addOns}
          step={step}
          onChange={handleAttendeeAddOnChange}
        />
      ))}
    </SectionLayout>
  )
}

AddOns.propTypes = reservationStepPropTypes

export default AddOns
